'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import type { Project } from '@inno-ck/shared';
import { isTextDocData } from '@inno-ck/shared';
import { patchProject } from '@/shell/services/api.service';
import { useAutosave, useClickOutside, useFileUpload } from '@/shell/hooks';
import { TEXTDOC_TEMPLATES } from './textdoc-templates';

interface TextDocCanvasProps {
  readonly project: Project;
}

export default function TextDocCanvas({ project }: TextDocCanvasProps) {
  const { schedule: scheduleSave, status: saving } = useAutosave<unknown>(
    json => patchProject(project.id, {
      jsonData: { projectType: 'textdoc', content: json },
    } as unknown as Partial<Project>),
  );

  const initialContent = isTextDocData(project.jsonData) ? project.jsonData.content : null;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: { openOnClick: false, autolink: true, linkOnPaste: true },
      }),
      Highlight,
      Image.configure({ inline: false, allowBase64: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({
        placeholder: 'Commencez à rédiger votre document…',
      }),
    ],
    content: initialContent ?? '',
    editorProps: {
      attributes: {
        class: 'textdoc-editor focus:outline-none min-h-[600px] px-14 py-12',
      },
    },
    immediatelyRender: false,
  });

  // Reload content when switching between textdoc projects
  useEffect(() => {
    if (!editor) return;
    const content = isTextDocData(project.jsonData) ? project.jsonData.content : null;
    editor.commands.setContent(content ?? '');
  }, [project.id, editor]);

  useEffect(() => {
    if (!editor) return;
    const onUpdate = (): void => scheduleSave(editor.getJSON());
    editor.on('update', onUpdate);
    return () => { editor.off('update', onUpdate); };
  }, [editor, scheduleSave]);

  const { inputRef: fileInputRef, pick: handleImageUpload, onChange: onFilePicked } = useFileUpload(
    src => { editor?.chain().focus().setImage({ src }).run(); },
  );

  const addLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL du lien', previous ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  // Load a template into the editor. Warns the user if there is already
  // content — they can either replace the doc or cancel.
  const loadTemplate = useCallback((templateId: string) => {
    if (!editor) return;
    const tpl = TEXTDOC_TEMPLATES.find(t => t.id === templateId);
    if (!tpl) return;
    const hasContent = !editor.isEmpty;
    if (hasContent) {
      const confirmed = window.confirm(
        `Le document contient déjà du contenu. Le remplacer par le template « ${tpl.label} » ?`
      );
      if (!confirmed) return;
    }
    editor.chain().focus().setContent(tpl.html).run();
  }, [editor]);

  const [showTemplateMenu, setShowTemplateMenu] = useState<boolean>(false);
  const templateBtnRef = useRef<HTMLDivElement | null>(null);
  useClickOutside(templateBtnRef, () => setShowTemplateMenu(false), showTemplateMenu);

  const exportPdf = useCallback(async () => {
    if (!editor) return;
    const mod = await import('html2pdf.js');
    // html2pdf's shipped types claim html2pdf() returns Promise<void>, but at
    // runtime calling it with no arguments returns a chainable Worker. Cast
    // to a permissive callable to access .set().from().save().
    const html2pdf = mod.default as unknown as () => {
      set: (opts: Record<string, unknown>) => { from: (el: HTMLElement) => { save: () => Promise<void> } };
    };
    const container = document.createElement('div');
    container.className = 'textdoc-pdf-export';
    container.innerHTML = editor.getHTML();
    container.style.cssText = 'padding: 40px; font-family: Georgia, serif; color: #1a1a1a; max-width: 780px; line-height: 1.55;';
    document.body.appendChild(container);
    try {
      await html2pdf()
        .set({
          margin: [12, 12, 12, 12],
          filename: `${project.projectName || 'document'}.pdf`,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(container)
        .save();
    } finally {
      container.remove();
    }
  }, [editor, project.projectName]);

  if (!editor) return <div className="flex-1 bg-[#f7f7f7]" />;

  return (
    <div className="flex-1 flex flex-col bg-[#f0f2f5] overflow-hidden">
      {/* Editor toolbar. The tagline badge lives at the bottom-right of
          the screen so the toolbar has the full width to itself. */}
      <div className="flex items-center gap-1 px-3 py-1.5 bg-white border-b border-[#e0e0e0] flex-wrap shadow-sm">
        {/* Template picker — one-click insert of a pre-structured document */}
        <div className="relative" ref={templateBtnRef}>
          <button
            onClick={() => setShowTemplateMenu(v => !v)}
            className="flex items-center gap-1.5 h-8 px-2.5 rounded text-[#333] text-[12px] font-medium hover:bg-[#f0f0f0] transition-colors"
            title="Insérer un template"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="9" y1="13" x2="15" y2="13"/>
              <line x1="9" y1="17" x2="15" y2="17"/>
            </svg>
            Templates
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          {showTemplateMenu && (
            <div className="absolute top-[calc(100%+4px)] left-0 bg-white border border-[#ddd] rounded-md shadow-lg z-[500] min-w-[280px] py-1"
                 onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              {TEXTDOC_TEMPLATES.map(tpl => (
                <div key={tpl.id}
                  className="flex flex-col px-3.5 py-2 cursor-pointer text-[13px] gap-[2px] hover:bg-[#f4f4f4]"
                  onClick={() => { setShowTemplateMenu(false); loadTemplate(tpl.id); }}>
                  <span className="font-medium text-[#222]">📋 {tpl.label}</span>
                  <span className="text-[11px] text-[#888]">{tpl.description}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <Divider />
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Titre 1">
          <span className="font-bold text-base">H1</span>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Titre 2">
          <span className="font-bold text-sm">H2</span>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Titre 3">
          <span className="font-bold text-xs">H3</span>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive('paragraph')} title="Paragraphe">
          <span className="text-xs">¶</span>
        </ToolbarButton>
        <Divider />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Gras (Ctrl+B)">
          <span className="font-bold">B</span>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italique (Ctrl+I)">
          <span className="italic font-serif">I</span>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Souligné (Ctrl+U)">
          <span className="underline">U</span>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Barré">
          <span className="line-through">S</span>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHighlight().run()} active={editor.isActive('highlight')} title="Surligner">
          <span className="bg-yellow-200 px-0.5">H</span>
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} title="Code">
          <span className="font-mono text-xs">{'<>'}</span>
        </ToolbarButton>
        <Divider />
        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Liste à puces">
          <IconBulletList />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Liste numérotée">
          <IconOrderedList />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Citation">
          <IconQuote />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Bloc de code">
          <IconCodeBlock />
        </ToolbarButton>
        <Divider />
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Aligner à gauche">
          <IconAlignLeft />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Centrer">
          <IconAlignCenter />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Aligner à droite">
          <IconAlignRight />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('justify').run()} active={editor.isActive({ textAlign: 'justify' })} title="Justifier">
          <IconAlignJustify />
        </ToolbarButton>
        <Divider />
        <ToolbarButton onClick={addLink} active={editor.isActive('link')} title="Lien">
          <IconLink />
        </ToolbarButton>
        <ToolbarButton onClick={handleImageUpload} title="Insérer une image">
          <IconImage />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Ligne de séparation">
          <span className="text-xs">―</span>
        </ToolbarButton>
        <Divider />
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Annuler (Ctrl+Z)">
          <IconUndo />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rétablir (Ctrl+Y)">
          <IconRedo />
        </ToolbarButton>
        <Divider />
        <button
          onClick={exportPdf}
          className="flex items-center gap-1.5 bg-[#e05a00] hover:bg-[#c44f00] text-white rounded px-3 h-8 text-[12px] font-semibold"
          title="Exporter en PDF"
        >
          <IconDownload /> PDF
        </button>
        <span className="ml-auto text-[11px] text-[#888]">
          {saving === 'saving' && 'Enregistrement…'}
          {saving === 'saved' && 'Enregistré'}
        </span>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onFilePicked}
          className="hidden"
        />
      </div>
      {/* Paper-like editor area. The visual pagination is decorative (CSS
          gradient every 1056 px ≈ one A4 content area at 96 DPI). Real
          page-break measurement like Google Docs requires a second render
          pass and is out of scope here. */}
      {/* Paper sits inside a scrollable grey canvas. The sheet starts at
          viewport-height so you never see grey at the bottom on a blank
          doc, and grows as content is added — giving the "continuous page"
          feel without page-break lines. */}
      <div className="flex-1 overflow-auto bg-[#e6e8ec]">
        <div className="flex justify-center pt-10 px-6 pb-10 min-h-full">
          <div className="textdoc-paper w-[816px] max-w-full min-h-[calc(100vh-120px)] rounded-sm">
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toolbar primitives
// ---------------------------------------------------------------------------

function ToolbarButton({
  onClick, active, disabled, title, children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={[
        'w-8 h-8 flex items-center justify-center rounded text-[#333] text-sm transition-colors',
        active ? 'bg-[#e0e7ff] text-[#1e40af]' : 'hover:enabled:bg-[#f0f0f0]',
        disabled ? 'opacity-35 cursor-default' : 'cursor-pointer',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-5 bg-[#e0e0e0] mx-0.5" />;
}

// ---------------------------------------------------------------------------
// Icons (inline SVG)
// ---------------------------------------------------------------------------

const SVG = ({ children }: { children: React.ReactNode }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const IconBulletList = () => <SVG><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></SVG>;
const IconOrderedList = () => <SVG><line x1="10" y1="6" x2="21" y2="6" /><line x1="10" y1="12" x2="21" y2="12" /><line x1="10" y1="18" x2="21" y2="18" /><path d="M4 6h1v4" /><path d="M4 10h2" /><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" /></SVG>;
const IconQuote = () => <SVG><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.75-2-2-2H4c-.75 0-1 .25-1 1v10c0 .75.25 1 1 1h3c0 2-1 4-4 4" /><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.75-2-2-2h-4c-.75 0-1 .25-1 1v10c0 .75.25 1 1 1h3c0 2-1 4-4 4" /></SVG>;
const IconCodeBlock = () => <SVG><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></SVG>;
const IconAlignLeft = () => <SVG><line x1="17" y1="10" x2="3" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="17" y1="18" x2="3" y2="18" /></SVG>;
const IconAlignCenter = () => <SVG><line x1="18" y1="10" x2="6" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="18" y1="18" x2="6" y2="18" /></SVG>;
const IconAlignRight = () => <SVG><line x1="21" y1="10" x2="7" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="21" y1="18" x2="7" y2="18" /></SVG>;
const IconAlignJustify = () => <SVG><line x1="21" y1="10" x2="3" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="21" y1="18" x2="3" y2="18" /></SVG>;
const IconLink = () => <SVG><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></SVG>;
const IconImage = () => <SVG><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></SVG>;
const IconUndo = () => <SVG><path d="M3 7v6h6" /><path d="M21 17a9 9 0 0 0-15-6.7L3 13" /></SVG>;
const IconRedo = () => <SVG><path d="M21 7v6h-6" /><path d="M3 17a9 9 0 0 1 15-6.7L21 13" /></SVG>;
// Export / upload icon — tray with an arrow going UP and OUT (PDF leaves the app).
const IconDownload = () => <SVG><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></SVG>;
